import { Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { lazyWithRetry as lazy } from "./lib/lazyRetry";
import { FullPageLoader, GuestOnly, LandingRedirect, RequireAuth, RequirePortal, RequireStaff } from "./routes/guards";
import Login from "./pages/auth/Login";

const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const AcceptInvite = lazy(() => import("./pages/auth/AcceptInvite"));
const OrgPicker = lazy(() => import("./pages/platform/OrgPicker"));
const Superadmins = lazy(() => import("./pages/platform/Superadmins"));
const Profile = lazy(() => import("./pages/platform/Profile"));
const Welcome = lazy(() => import("./pages/platform/Welcome"));
const NotFound = lazy(() => import("./pages/platform/NotFound"));
const OrgLayout = lazy(() => import("./pages/org/OrgLayout"));
const Properties = lazy(() => import("./pages/org/Properties"));
const OrgUsers = lazy(() => import("./pages/org/OrgUsers"));
const OrgEmails = lazy(() => import("./pages/org/OrgEmails"));
const OrgSettings = lazy(() => import("./pages/org/OrgSettings"));
const PropertyLayout = lazy(() => import("./pages/property/PropertyLayout"));
const PropertyHome = lazy(() => import("./pages/property/PropertyHome"));
const PropertySettings = lazy(() => import("./pages/property/PropertySettings"));
const ComingSoon = lazy(() => import("./pages/property/ComingSoon"));
const LeasesPage = lazy(() => import("./pages/property/leases/LeasesPage"));
const LeaseDetailPage = lazy(() => import("./pages/property/leases/LeaseDetailPage"));
const CostsPage = lazy(() => import("./pages/property/costs/CostsPage"));
const BillingPage = lazy(() => import("./pages/property/billing/BillingPage"));
const MetersPage = lazy(() => import("./pages/property/meters/MetersPage"));
const ReconciliationPage = lazy(() => import("./pages/property/reconciliation/ReconciliationPage"));
const ReportsPage = lazy(() => import("./pages/property/reports/ReportsPage"));
const PortalLayout = lazy(() => import("./pages/portal/PortalLayout"));
const PortalHome = lazy(() => import("./pages/portal/PortalHome"));
const PortalSoon = lazy(() => import("./pages/portal/PortalSoon"));
const PortalProfile = lazy(() => import("./pages/portal/PortalProfile"));

export default function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <Suspense fallback={<FullPageLoader />}>
        <Routes>
          <Route element={<GuestOnly />}>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
          </Route>
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/invite/:token" element={<AcceptInvite />} />
          <Route path="/" element={<LandingRedirect />} />

          <Route element={<RequireAuth />}>
            <Route path="/welcome" element={<Welcome />} />

            <Route element={<RequirePortal />}>
              <Route path="/portal" element={<PortalLayout />}>
                <Route index element={<PortalHome />} />
                <Route path="profile" element={<PortalProfile />} />
                <Route path="*" element={<PortalSoon />} />
              </Route>
            </Route>

            <Route element={<RequireStaff />}>
              <Route path="/orgs" element={<OrgPicker />} />
              <Route path="/admin/superadmins" element={<Superadmins />} />
              <Route path="/profile" element={<Profile />} />

              <Route path="/o/:slug" element={<OrgLayout />}>
                <Route index element={<Navigate to="properties" replace />} />
                <Route path="properties" element={<Properties />} />
                <Route path="users" element={<OrgUsers />} />
                <Route path="emails" element={<OrgEmails />} />
                <Route path="settings/:tab?" element={<OrgSettings />} />
                <Route path="p/:propertyId" element={<PropertyLayout />}>
                  <Route index element={<PropertyHome />} />
                  <Route path="settings" element={<PropertySettings />} />
                  <Route path="leases" element={<LeasesPage />} />
                  <Route path="leases/:leaseId" element={<LeaseDetailPage />} />
                  <Route path="costs" element={<CostsPage />} />
                  <Route path="billing" element={<BillingPage />} />
                  <Route path="meters" element={<MetersPage />} />
                  <Route path="reconciliation" element={<ReconciliationPage />} />
                  <Route path="reports" element={<ReportsPage />} />
                  <Route path=":section" element={<ComingSoon />} />
                </Route>
              </Route>
            </Route>
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
